import { api, unwrap } from "../api/client";

export interface LayerStyle { color: string; width?: number; dash?: number[]; opacity?: number; outline?: string | null }
export type Styles = Record<string, LayerStyle>;

/**
 * Fallback when the server cannot be reached; the reference values are those of
 * core-model symbology-default.json, served by GET /symbology with the administrator's overrides.
 */
export const FALLBACK: Styles = {
  CLOSED_ALL: { color: "#212529", width: 6 },
  PUBLIC_CLOSED: { color: "#e8590c", width: 6, dash: [2, 1] },
  ENCLOSED: { color: "#f08c00", width: 2.5, opacity: 0.85 },
  INACCESSIBLE: { color: "#868e96", width: 2, dash: [1, 2] },
  EMERGENCY_ACCESS: { color: "#e03131", width: 6 },
  ONEWAY_LIFTED: { color: "#2b8a3e", width: 4, opacity: 0.8 },
  ONEWAY_REVERSED: { color: "#7048e8", width: 4, opacity: 0.8 },
  COURSE: { color: "#d9480f", width: 4 },
  HEIGHT_OVERRIDE: { color: "#495057", width: 4 },
  SOFT_OBSTACLE: { color: "#868e96", width: 5, dash: [0.5, 0.5] },
  BARRIER_NON_CROSSABLE: { color: "#c92a2a", width: 7, outline: "#ffffff" },
  BARRIER_CROSSABLE: { color: "#e8590c", width: 7, outline: "#ffffff" },
  DRAWN: { color: "#0b2e6b", width: 2, opacity: 0.7 },
  POI: { color: "#2f9e44", width: 8, outline: "#ffffff" },
  ROUTE: { color: "#1d4ed8", width: 5, outline: "#ffffff" },
  ALTERNATIVE: { color: "#495057", width: 4, dash: [2, 1.5], opacity: 0.8 },
  WITHOUT_RESTRICTIONS: { color: "#9c36b5", width: 3, dash: [1, 1.5] },
};

export async function loadSymbology(): Promise<Styles> {
  try {
    const r = unwrap(await api.GET("/symbology"));
    return { ...FALLBACK, ...(r.styles as Styles) };
  } catch {
    return FALLBACK;
  }
}

/** CSS of a legend swatch drawn like the map line */
export function swatchStyle(s: LayerStyle): React.CSSProperties {
  const w = Math.max(2, Math.min(6, s.width ?? 4));
  const dash = s.dash ?? [];
  return {
    borderTopColor: s.color,
    borderTopWidth: w,
    borderTopStyle: dash.length === 0 ? "solid" : dash[0] < 1 ? "dotted" : "dashed",
    opacity: s.opacity ?? 1,
  };
}
